import React, { useRef, useState } from 'react';
import { Component, FormValues } from '@/types/platform';
import MobileUploadQrModal from './MobileUploadQrModal';
import './ComponentRenderer.css';

interface ComponentRendererProps {
  component: Component;
  value: any;
  files: File[];
  formValues: FormValues;
  onValueChange: (name: string, value: any) => void;
  onFileChange: (name: string, files: File[]) => void;
  getTranslation: (key: string) => string;
}

const CUSTOM_VALUE_KEY = '__custom__';

const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  component,
  value,
  files,
  formValues,
  onValueChange,
  onFileChange,
  getTranslation,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const colSpanClass = component.colSpan ? `col-span-${component.colSpan}` : '';
  
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);

  const handleSelectChange = (selectedValue: string) => {
    if (component.allowCustomValue && selectedValue === CUSTOM_VALUE_KEY) {
      setIsCustomMode(true);
      setCustomValue('');
      onValueChange(component.name, '');
    } else {
      setIsCustomMode(false);
      setCustomValue('');
      onValueChange(component.name, selectedValue);
    }
  };

  const handleCustomValueChange = (inputValue: string) => {
    setCustomValue(inputValue);
    onValueChange(component.name, inputValue);
  };

  const renderSelect = () => (
    <div className={`form-field ${colSpanClass}`}>
      <label htmlFor={component.name}>
        {getTranslation(component.labelKey)}
        {component.required && <span className="required">*</span>}
      </label>
      {component.descriptionKey && (
        <p className="field-description">{getTranslation(component.descriptionKey)}</p>
      )}
      <select
        id={component.name}
        name={component.name}
        value={isCustomMode ? CUSTOM_VALUE_KEY : (value || '')}
        onChange={(e) => handleSelectChange(e.target.value)}
        required={component.required && !isCustomMode}
      >
        {!component.required && <option value="">Изберете...</option>}
        {component.options?.map((option) => (
          <option key={option.value} value={option.value}>
            {getTranslation(option.labelKey)}
          </option>
        ))}
        {component.allowCustomValue && (
          <option value={CUSTOM_VALUE_KEY}>{getTranslation('customValue.option')}</option>
        )}
      </select>
      
      {isCustomMode && (
        <input
          type="text"
          className="custom-value-input"
          placeholder={getTranslation('customValue.placeholder')}
          value={customValue}
          onChange={(e) => handleCustomValueChange(e.target.value)}
          required={component.required}
        />
      )}
    </div>
  );

  const renderCheckbox = () => (
    <div className={`form-field form-field-checkbox ${colSpanClass}`}>
      <label>
        <input
          type="checkbox"
          name={component.name}
          checked={value || component.default || false}
          onChange={(e) => onValueChange(component.name, e.target.checked)}
        />
        <span>
          {getTranslation(component.labelKey)}
          {component.required && <span className="required">*</span>}
        </span>
      </label>
      {component.descriptionKey && (
        <p className="field-description">{getTranslation(component.descriptionKey)}</p>
      )}
    </div>
  );

  const getAcceptedExtensions = (): string[] | null => {
    if (!component.accept && !component.acceptOverrides) return null;
    const documentType = String(formValues['documentType'] ?? '');
    if (component.acceptOverrides && documentType && component.acceptOverrides[documentType]) {
      return component.acceptOverrides[documentType];
    }
    return component.accept ?? null;
  };

  const getAcceptAttribute = (): string | undefined => {
    const exts = getAcceptedExtensions();
    return exts ? exts.join(',') : undefined;
  };

  const handleFileRemove = (indexToRemove: number) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);
    onFileChange(component.name, updatedFiles);
  };

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const allowedExtensions = getAcceptedExtensions();

    // Validate file extensions if restrictions are defined
    if (allowedExtensions) {
      const rejected: string[] = [];
      const valid = newFiles.filter(file => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (allowedExtensions.includes(ext)) return true;
        rejected.push(ext);
        return false;
      });

      if (rejected.length > 0) {
        setFileTypeError(`${getTranslation('upload.unsupportedFileType')} ${[...new Set(rejected)].join(', ')}`);
      } else {
        setFileTypeError(null);
      }

      if (valid.length === 0) {
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      const toAdd = valid;
      if (component.multiple) {
        const existingNames = new Set(files.map(f => f.name));
        const unique = toAdd.filter(f => !existingNames.has(f.name));
        onFileChange(component.name, [...files, ...unique]);
      } else {
        onFileChange(component.name, toAdd);
      }
    } else {
      setFileTypeError(null);
      if (component.multiple) {
        const existingNames = new Set(files.map(f => f.name));
        const unique = newFiles.filter(f => !existingNames.has(f.name));
        onFileChange(component.name, [...files, ...unique]);
      } else {
        onFileChange(component.name, newFiles);
      }
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const renderFileUpload = () => {
    const mobileEnabled = component.uploadSources?.mobileCamera?.enabled === true;

    const handleUploadButtonClick = () => {
      if (mobileEnabled) {
        setShowSourceDropdown(!showSourceDropdown);
      } else {
        fileInputRef.current?.click();
      }
    };

    const handleLocalUploadClick = () => {
      setShowSourceDropdown(false);
      fileInputRef.current?.click();
    };

    const handleMobileUploadClick = () => {
      setShowSourceDropdown(false);
      setShowQrModal(true);
    };

    const handleMobileFilesReceived = (mobileFiles: File[]) => {
      if (component.multiple) {
        const existingNames = new Set(files.map(f => f.name));
        const unique = mobileFiles.filter(f => !existingNames.has(f.name));
        onFileChange(component.name, [...files, ...unique]);
      } else {
        onFileChange(component.name, mobileFiles);
      }
    };

    return (
      <div className={`form-field ${colSpanClass}`}>
        <label htmlFor={component.name}>
          {getTranslation(component.labelKey)}
          {component.required && <span className="required">*</span>}
        </label>
        {component.descriptionKey && (
          <p className="field-description">{getTranslation(component.descriptionKey)}</p>
        )}
        <input
          type="file"
          ref={fileInputRef}
          id={component.name}
          name={component.name}
          multiple={component.multiple}
          required={component.required && files.length === 0}
          className="file-input-hidden"
          accept={getAcceptAttribute()}
          onChange={handleFileAdd}
        />
        <div className="upload-button-wrapper">
          <button
            type="button"
            className="upload-button"
            onClick={handleUploadButtonClick}
          >
            {files.length > 0 ? getTranslation('upload.addFiles') : getTranslation(component.labelKey)}
          </button>
          {mobileEnabled && showSourceDropdown && (
            <div className="upload-source-dropdown">
              <button
                type="button"
                className="upload-source-option"
                onClick={handleLocalUploadClick}
              >
                {getTranslation('upload.sources.local')}
              </button>
              <button
                type="button"
                className="upload-source-option"
                onClick={handleMobileUploadClick}
              >
                {getTranslation('upload.sources.mobileCamera')}
              </button>
            </div>
          )}
        </div>
        {fileTypeError && (
          <p className="file-type-error">{fileTypeError}</p>
        )}
        {files.length > 0 && (
          <div className="file-list">
            {files.map((file, index) => (
              <div key={index} className="file-item">
                <span className="file-name">{file.name}</span>
                <button
                  type="button"
                  className="file-remove-button"
                  onClick={() => handleFileRemove(index)}
                  aria-label={getTranslation('upload.removeFile')}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* QR Modal for mobile upload */}
        {showQrModal && component.uploadSources?.mobileCamera && (
          <MobileUploadQrModal
            config={component.uploadSources.mobileCamera}
            getTranslation={getTranslation}
            onFilesReceived={handleMobileFilesReceived}
            onClose={() => setShowQrModal(false)}
          />
        )}
      </div>
    );
  };

  const renderText = () => (
    <div className={`form-field ${colSpanClass}`}>
      <label htmlFor={component.name}>
        {getTranslation(component.labelKey)}
        {component.required && <span className="required">*</span>}
      </label>
      {component.descriptionKey && (
        <p className="field-description">{getTranslation(component.descriptionKey)}</p>
      )}
      <input
        type="text"
        id={component.name}
        name={component.name}
        value={value ?? ''}
        maxLength={component.maxLength}
        required={component.required}
        placeholder={component.placeholderKey ? getTranslation(component.placeholderKey) : undefined}
        onChange={(e) => onValueChange(component.name, e.target.value)}
      />
    </div>
  );

  const renderNumber = () => (
    <div className={`form-field ${colSpanClass}`}>
      <label htmlFor={component.name}>
        {getTranslation(component.labelKey)}
        {component.required && <span className="required">*</span>}
      </label>
      {component.descriptionKey && (
        <p className="field-description">{getTranslation(component.descriptionKey)}</p>
      )}
      <input
        type="number"
        id={component.name}
        name={component.name}
        value={value ?? ''}
        min={component.min}
        step={component.step}
        required={component.required}
        placeholder={component.placeholderKey ? getTranslation(component.placeholderKey) : undefined}
        onChange={(e) => onValueChange(component.name, e.target.value)}
      />
    </div>
  );

  const renderDate = () => (
    <div className={`form-field ${colSpanClass}`}>
      <label htmlFor={component.name}>
        {getTranslation(component.labelKey)}
        {component.required && <span className="required">*</span>}
      </label>
      {component.descriptionKey && (
        <p className="field-description">{getTranslation(component.descriptionKey)}</p>
      )}
      <input
        type="date"
        id={component.name}
        name={component.name}
        value={value ?? ''}
        required={component.required}
        onChange={(e) => onValueChange(component.name, e.target.value)}
      />
    </div>
  );

  switch (component.type) {
    case 'select':
      return renderSelect();
    case 'checkbox':
      return renderCheckbox();
    case 'fileUpload':
      return renderFileUpload();
    case 'text':
      return renderText();
    case 'number':
      return renderNumber();
    case 'date':
      return renderDate();
    default:
      return null;
  }
};

export default ComponentRenderer;
